import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, updateDoc, arrayUnion, arrayRemove, addDoc, collection } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../Auth/AuthContext';

const FollowingContext = createContext({ following: [], followUser: async () => {}, unfollowUser: async () => {} });

export const useFollowing = () => useContext(FollowingContext);

export const FollowingProvider = ({ children }) => {
    const { userId } = useAuth();
    const [following, setFollowing] = useState([]);

    // Escucha en tiempo real el doc del usuario actual
    useEffect(() => {
        if (!userId) { setFollowing([]); return; }
        const unsub = onSnapshot(doc(db, 'users', userId), (snap) => {
            if (snap.exists()) {
                setFollowing(snap.data().following || []);
            }
        });
        return () => unsub();
    }, [userId]);

    const followUser = useCallback(async (targetId) => {
        if (!userId) return;
        // NO optimistic update — onSnapshot dispara inmediatamente cuando
        // Firestore aplica el write al cache local (antes de confirmación servidor)
        try {
            await Promise.all([
                updateDoc(doc(db, 'users', userId),   { following:  arrayUnion(targetId) }),
                updateDoc(doc(db, 'users', targetId), { followers: arrayUnion(userId) }),
            ]);
            await addDoc(collection(db, 'notifications'), {
                type: 'follow',
                recipientId: targetId,
                senderId: userId,
                read: false,
                timestamp: new Date(),
            });
        } catch (e) {
            console.error('[followUser]', e);
        }
    }, [userId]);

    const unfollowUser = useCallback(async (targetId) => {
        if (!userId) return;
        try {
            await Promise.all([
                updateDoc(doc(db, 'users', userId),   { following:  arrayRemove(targetId) }),
                updateDoc(doc(db, 'users', targetId), { followers: arrayRemove(userId) }),
            ]);
        } catch (e) {
            console.error('[unfollowUser]', e);
        }
    }, [userId]);

    return (
        <FollowingContext.Provider value={{ following, followUser, unfollowUser }}>
            {children}
        </FollowingContext.Provider>
    );
};
